import type { FC, ReactNode } from 'react'
import type { VariantProps } from 'class-variance-authority'
import type { BookingSelection, DayAvailability } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'

import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import BookingDialog from '@/components/landing/booking-dialog'

type BookingContextValue = {
    days: DayAvailability[]
    openBooking: (selection?: BookingSelection) => void
}

const BookingContext = createContext<BookingContextValue | null>(null)

/** Returns null on pages rendered without a BookingProvider */
export const useBooking = (): BookingContextValue | null => {
    return useContext(BookingContext)
}

interface BookingProviderProps {
    days: DayAvailability[]
    children: ReactNode
}

export const BookingProvider: FC<BookingProviderProps> = ({ days: initialDays, children }): ReactNode => {
    const [days, setDays] = useState<DayAvailability[]>(initialDays)
    const [open, setOpen] = useState(false)
    const [selection, setSelection] = useState<BookingSelection>({})
    const [dialogKey, setDialogKey] = useState(0)

    const refreshAvailability = useCallback(async (): Promise<void> => {
        try {
            const response = await fetch('/api/ketersediaan', { cache: 'no-store' })

            if (!response.ok) return

            const data = await response.json() as { days?: DayAvailability[] }

            if (Array.isArray(data.days) && data.days.length > 0) setDays(data.days)
        }
        catch {
            // Keep the availability that is already on screen.
        }
    }, [])

    const openBooking = useCallback((next: BookingSelection = {}): void => {
        setSelection(next)
        setDialogKey(value => value + 1)
        setOpen(true)
        void refreshAvailability()
    }, [refreshAvailability])

    const value = useMemo(() => ({ days, openBooking }), [days, openBooking])

    return (
        <BookingContext.Provider value={value}>
            {children}

            <BookingDialog
                key={dialogKey}
                open={open}
                onOpenChange={setOpen}
                days={days}
                initial={selection}
                onBooked={refreshAvailability}
            />
        </BookingContext.Provider>
    )
}

interface BookButtonProps extends VariantProps<typeof buttonVariants> {
    selection?: BookingSelection
    className?: string
    children: ReactNode
}

/** Opens the booking dialog, or links to the availability section on pages without the dialog */
export const BookButton: FC<BookButtonProps> = ({
    selection,
    className,
    variant,
    size,
    children
}): ReactNode => {
    const booking = useBooking()

    if (!booking) {
        return (
            <a href='/#ketersediaan' className={cn(buttonVariants({ variant, size }), className)}>
                {children}
            </a>
        )
    }

    return (
        <Button
            variant={variant}
            size={size}
            className={className}
            onClick={() => booking.openBooking(selection)}
        >
            {children}
        </Button>
    )
}
